import { MARKETPLACE_CURRENCY_CODE } from "./marketplaceConstants";

export const formatNumber = (
  number: number | string | null | undefined,
  options: {
    default?: string;
    rounded?: "up" | "down";
    currency?: { marketPlace: keyof typeof MARKETPLACE_CURRENCY_CODE | (string & {}) };
    compact?: boolean;
    addPlus?: boolean;
    decimalPlaces?: number;
  } | null = {},
) => {
  if (!options) {
    options = {};
  }
  if (number == null || isNaN(parseFloat(number.toString()))) {
    return options.default ?? "-";
  }

  let parsedNumber = parseFloat(number.toString());

  if (options.rounded) {
    if (options.rounded === "up") {
      parsedNumber = Math.ceil(parsedNumber);
    } else {
      parsedNumber = Math.round(parsedNumber);
    }
  }

  if (options.decimalPlaces) {
    parsedNumber = parseFloat(parsedNumber.toFixed(options.decimalPlaces));
  }

  if (options.decimalPlaces === 0 || parsedNumber % 1 === 0) {
    parsedNumber = parseFloat(parsedNumber.toFixed(0));
  }

  if (options.currency) {
    return formatCurrency(parseFormattedNumber(number), options.currency.marketPlace);
  }

  let addK = false;
  if (options.compact && parsedNumber > 10000) {
    parsedNumber = Math.round(parsedNumber / 10000);
    addK = true;
  }

  let formattedNumber = new Intl.NumberFormat().format(parsedNumber);

  if (parsedNumber > 0 && options.addPlus) {
    formattedNumber = `+${formattedNumber}`;
  }

  if (options.compact && addK) {
    formattedNumber = `${formattedNumber}K`;
  }

  return formattedNumber;
};

export const parseFormattedNumber = (number: number | string) => {
  if (typeof number === "number") {
    return number;
  }
  const parsedNumber = parseFloat(number.replace(/,/g, ""));

  return parsedNumber;
};

export const parseFormattedPercentage = (number: string) => {
  const parsedNumber = parseFloat(number.replace(/%,/g, ""));

  return parsedNumber;
};

export const parseFormattedCurrency = (number: string) => {
  const parsedCurrency = parseInt(number);

  return parsedCurrency;
};

export const formatCurrency = (
  number: number,
  marketPlace?: string,
  clipDecimalPlaces = false,
  addPlus = false,
  compact = false,
) => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    currency: MARKETPLACE_CURRENCY_CODE[marketPlace as keyof typeof MARKETPLACE_CURRENCY_CODE] ?? "USD",
    maximumFractionDigits: clipDecimalPlaces ? 0 : 2,
    minimumFractionDigits: clipDecimalPlaces ? 0 : 2,
    currencyDisplay: "narrowSymbol",
  };
  if (compact) {
    options["notation"] = "compact";
  }
  let formattedCurrency = new Intl.NumberFormat(navigator.languages, options).format(number);

  if (number > 0 && addPlus) {
    formattedCurrency = `+${formattedCurrency}`;
  }

  if (formattedCurrency.includes("NaN")) {
    return "-";
  }

  return formattedCurrency;
};

export const formatPercent = (number: number, decimalPlaces = 0, addPlus?: boolean) => {
  const percent = new Intl.NumberFormat(navigator.language, {
    style: "percent",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(number);

  if (Number.isNaN(number)) {
    return "-";
  }

  if (number > 0 && addPlus) {
    return `+${percent}`;
  }

  if (percent.includes("NaN")) {
    return "-";
  }

  return percent;
};

export const getCurrencySymbol = (marketPlace: string | undefined) => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    currency: MARKETPLACE_CURRENCY_CODE[marketPlace as keyof typeof MARKETPLACE_CURRENCY_CODE] ?? "USD",
    currencyDisplay: "symbol",
  };
  const formatter = new Intl.NumberFormat(navigator.languages, options);
  // Use a number to get the formatted currency
  const parts = formatter.formatToParts(0);
  // Extract the symbol part
  const currencyPart = parts.find((part) => part.type === "currency");
  const currencySymbol = currencyPart ? currencyPart.value : "";
  return currencySymbol;
};

const formatters = {
  currency: formatCurrency,
  percent: formatPercent,
  number: formatNumber,
};
export function formatMetricValue(value: number | null | undefined, formatOptions: FormatOptions, marketPlace: string) {
  if (value === null || value === undefined) return "-";

  let displayValue = value.toString();
  switch (formatOptions.format) {
    case "currency":
      displayValue = formatters.currency(
        value,
        marketPlace,
        formatOptions.clipDecimalPlaces,
        formatOptions.addPlus,
        formatOptions.compact,
      );
      break;
    case "percent":
      displayValue = formatters.percent(value, formatOptions.decimalPlaces, formatOptions.addPlus);
      break;
    case "number":
      displayValue = formatters.number(value, formatOptions);
      break;
  }
  return displayValue;
}
export type FormatOptions =
  | {
      format: "currency";
      addPlus?: boolean;
      clipDecimalPlaces?: boolean;
      compact?: boolean;
      decimalPlaces?: number;
      overrideMarketplace?: string;
    }
  | {
      format: "percent";
      addPlus?: boolean;
      decimalPlaces?: number;
      overrideMarketplace?: string;
    }
  | {
      format: "number";
      addPlus?: boolean;
      compact?: boolean;
      decimalPlaces?: number;
      default?: string;
      rounded?: "up" | "down";
      overrideMarketplace?: string;
    };
